import {
  TextField,
  Box,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Switch,
  ToggleButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { axiosGetCall, axiosPatchCall } from 'src/services/apisCall';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/iconify';
import Logger from 'src/lib/Logger';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { checkButtonAccess } from 'src/utils/common';

const logger = new Logger('asetting');

const requestList = [
  { label: 'Self', value: 'U' },
  { label: 'Downline', value: 'D' },
  { label: 'Hierarchy', value: 'H' },
];

const SwitchButton = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#65C466',
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.5,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 13,
    backgroundColor: theme.palette.grey[500],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 300,
    }),
  },
}));

const BasicDatePicker = ({ value, onChange, dateError, resetError, label }) => {
  const [, setOpen] = useState(false);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        views={['hours']}
        label={label}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true, // This makes it stretch to the container width
            error: Boolean(dateError),
            helperText: Boolean(dateError) && dateError,
            onClick: () => resetError(),
          },
          actionBar: {
            actions: ['today', 'clear'],
          },
        }}
        ampm={true}
        value={value}
        onChange={(newValue) => onChange(newValue)}
      />
    </LocalizationProvider>
  );
};


const SmsCampaignSetting = ({ user }) => {
  const inputSx = {
    color: 'grey.900',
    borderColor: 'grey.700',
    borderRadius: '6px',
  };


  const [RequestFor, setRequestFor] = useState('U');
  const [hyperRequestFor, setHyperRequestFor] = useState('U');
  const [apiPerMessageLimit, setApiPerMessageLimit] = useState('');
  const [hyperApiPerMessageLimit, setHyperApiPerMessageLimit] = useState('');
  const [isApiLimitUpdating, setIsApiLimitUpdating] = useState(false);
  const [isHyperApiLimitUpdating, setIsHyperApiLimitUpdating] = useState(false);
  const [detailDownloadCsvEnabled, setDetailDownloadCsvEnabled] = useState(false);
  const [isDetailDownloadCsvLoading, setIsDetailDownloadCsvLoading] = useState(true);
  const [isDetailDownloadCsvUpdating, setIsDetailDownloadCsvUpdating] = useState(false);
  const [customBroadcastApiEnabled, setCustomBroadcastApiEnabled] = useState(false);
  const [isCustomBroadcastApiLoading, setIsCustomBroadcastApiLoading] = useState(true);
  const [isCustomBroadcastApiUpdating, setIsCustomBroadcastApiUpdating] = useState(false);

  const [load, setLoad] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [callWindowloader, setCallWindowLoader] = useState(false);
  const [callWindowRequestFor, setCallWindowRequestFor] = useState("U");
  const [userTps, setUserTps] = useState('');
  const [userTpsLoader, setUserTpsLoader] = useState(false);

  const userId = user?.userId;
  const userType = user?.voiceInfo?.userType || user?.userType;


  const updatePerMinuteApiCallLimits = async () => {
    try {
      const payload = {
        userId: user?.userId,
        action: 'updatePerMinuteApiCallLimits',
        apiPerMinuteCallLimit: apiPerMessageLimit,
        requestFor: 'U',
      };

      if (user?.voiceInfo?.userType === 'reseller') {
        payload.requestFor = RequestFor;
      }

      setIsApiLimitUpdating(true);
      await axiosPatchCall('updateSmsProfileSetting', payload);
      fetchSettingDetails();
      renderSuccessMessage('Success');
    } catch (error) {
      logger.error(error);
      renderErrorMessage(error.message);
    } finally {
      setIsApiLimitUpdating(false);
    }
  };

  const updateHyperPerMinuteApiCallLimits = async () => {
    try {
      const payload = {
        userId: user?.userId,
        action: 'hyperUpdatePerMinuteApiCallLimits',
        hyperApiPerMinuteCallLimit: hyperApiPerMessageLimit,
        requestFor: 'U',
      };

      if (user?.voiceInfo?.userType === 'reseller') {
        payload.requestFor = hyperRequestFor;
      }

      setIsHyperApiLimitUpdating(true);
      await axiosPatchCall('updateSmsProfileSetting', payload);
      fetchSettingDetails();
      renderSuccessMessage('Success');
    } catch (error) {
      logger.error(error);
      renderErrorMessage(error.message);
    } finally {
      setIsHyperApiLimitUpdating(false);
    }
  };

  const fetchDetailDownloadCsvOption = async () => {
    if (!userId) return;

    setIsDetailDownloadCsvLoading(true);
    try {
      const { data = {} } = await axiosGetCall('getSmsUserSettingInfo', {
        requestName: 'getCsvDownloadDetailOption',
        requestUserId: userId,
      });
      const status = data?.csvDownloadDetailOption;
      setDetailDownloadCsvEnabled(
        status === true || status === 1 || status === '1' || status === 'true'
      );
    } catch (error) {
      logger.error(error);
      renderErrorMessage(error.message);
    } finally {
      setIsDetailDownloadCsvLoading(false);
    }
  };

  const handleDetailDownloadCsvChange = async (event) => {
    const previousStatus = detailDownloadCsvEnabled;
    const status = event.target.checked;

    setDetailDownloadCsvEnabled(status);
    setIsDetailDownloadCsvUpdating(true);
    try {
      const result = await axiosPatchCall('updateSmsProfileSetting', {
        action: 'updateDetailDownloadCsvOption',
        userId,
        userType,
        requestFor: 'U',
        status,
      });
      renderSuccessMessage(result?.message || 'Campaign details CSV download option updated');
    } catch (error) {
      logger.error(error);
      setDetailDownloadCsvEnabled(previousStatus);
      renderErrorMessage(error.message);
    } finally {
      setIsDetailDownloadCsvUpdating(false);
    }
  };

  const fetchCustomBroadcastApiAccess = async () => {
    if (!userId) return;

    setIsCustomBroadcastApiLoading(true);
    try {
      const { data = {} } = await axiosGetCall('getSmsUserSettingInfo', {
        requestName: 'allowHyperUpload',
        requestUserId: userId,
      });
      const status = data?.allowHyperUpload;
      setCustomBroadcastApiEnabled(
        status === true || status === 1 || status === '1' || status === 'true'
      );
    } catch (error) {
      logger.error(error);
      renderErrorMessage(error.message);
    } finally {
      setIsCustomBroadcastApiLoading(false);
    }
  };

  const handleCustomBroadcastApiAccessChange = async (event) => {
    const previousStatus = customBroadcastApiEnabled;
    const status = event.target.checked;

    setCustomBroadcastApiEnabled(status);
    setIsCustomBroadcastApiUpdating(true);
    try {
      const result = await axiosPatchCall('updateSmsProfileSetting', {
        action: 'updateHyperUploadAccess',
        userId,
        userType,
        requestFor: 'U',
        status,
      });
      renderSuccessMessage(result?.message || 'Custom Broadcast API access updated');
    } catch (error) {
      logger.error(error);
      setCustomBroadcastApiEnabled(previousStatus);
      renderErrorMessage(error.message);
    } finally {
      setIsCustomBroadcastApiUpdating(false);
    }
  };

  const updateAllocatedTps = async () => {
    const allocatedTps = Number(userTps);

    if (!userTps || !Number.isInteger(allocatedTps) || allocatedTps <= 0) {
      renderErrorMessage('User TPS must be a positive integer');
      return;
    }
    setUserTpsLoader(true);

    const payload = {
      userId: user?.userId,
      userType: user?.voiceInfo?.userType || user?.userType,
      requestFor: 'U',
      action: 'updateAllocatedTps',
      allocatedTps,
    };
    try {
      await axiosPatchCall('updateSmsProfileSetting', payload);
      fetchAllocatedTps();
      renderSuccessMessage('Success');
    } catch (error) {
      logger.error(error);
      renderErrorMessage(error.message);
    } finally {
      setUserTpsLoader(false);
    }
  };


  const handleReset = async (type) => {
    let payload = {
      userId: user?.userId,
      action: 'resetSetting',
      resetActionType: '',
      requestFor: 'U',
    };

    if (type === "apiPerCallLimit") {
      payload.resetActionType = "updatePerMinuteApiCallLimits";
      payload.requestFor = RequestFor;
    }
    if (type === 'hyperApiPerCallLimit') {
      payload.resetActionType = 'hyperUpdatePerMinuteApiCallLimits';
      payload.requestFor = hyperRequestFor;
    }
    if (type === "callWindow") {
    payload.resetActionType = "updateCallWindow";
    payload.requestFor = callWindowRequestFor;
    }
    if (type === 'userTps') {
      payload.resetActionType = 'updateAllocatedTps';
    }
    try {
      await axiosPatchCall('updateSmsProfileSetting', payload);
      renderSuccessMessage(`Success`);
      setLoad((prev) => !prev);
    } catch (error) {
      logger.error(error);
      renderErrorMessage(error.message);
    }
  };


  const handleSubmit = async (type) => {
    if (type === 'updatePerMinuteApiCallLimits') {
      await updatePerMinuteApiCallLimits();
    }
    if (type === 'hyperUpdatePerMinuteApiCallLimits') {
      await updateHyperPerMinuteApiCallLimits();
    }
    if (type === "callWindow") {
      await updateACallWindow();
    }
    if (type === 'userTps') {
      await updateAllocatedTps();
    }
  };

  const fetchSettingDetails = async () => {
    try {
      const { data } = await axiosGetCall('getSmsUserSettingInfo', {
        requestName: 'apiPerMinuteCallLimit',
        requestUserId: user?.userId,
      });
      setApiPerMessageLimit(data?.apiPerMinuteCallLimit || '');

      const { data: hyperData } = await axiosGetCall('getSmsUserSettingInfo', {
        requestName: 'hyperApiPerMinuteCallLimit',
        requestUserId: user?.userId,
      });
      setHyperApiPerMessageLimit(hyperData?.hyperApiPerMinuteCallLimit || '');

    } catch (error) {
      logger.error(error);
    }
  };

  const fetchAllocatedTps = async () => {
    try {
      const data = await axiosGetCall('getAllocatedTps', {
        userId: user?.userId,
      });

      setUserTps(data?.allocated_tps ?? '');
    } catch (error) {
      logger.error(error);
    }
  };

  const fetchCallWindowDetails = async () => {
          try {
              const params = { requestUserId: user?.userId, requestName: "callWindowStartEnd" }
              const { data = {} } = await axiosGetCall("getSmsUserSettingInfo", { ...params });
              
           
              const currentDate = dayjs();
              const startTime = currentDate.hour(data?.callWindowStartTime).minute(0).second(0).millisecond(0);
              const endTime = currentDate.hour(data?.callWindowEndTime).minute(0).second(0).millisecond(0);
  
              setStartTime(startTime);
              setEndTime(endTime);
          } catch (error) {
              logger.error(error);
          }
      };

  const resetStartTimeError = useMemo(() => {
    return () => {
      setStartDateError("");
    };
  }, []);

  const resetEndTimeError = useMemo(() => {
    return () => {
      setEndDateError("")
    }
  }, [])


  const updateACallWindow = async () => {
    try {
      if (!startTime) {
        return setStartDateError("Start date is required");
      }
      if (!endTime) {
        return setEndDateError("End date is required")
      }
      const startHour = dayjs(startTime).hour();
      const endHour = dayjs(endTime).hour();

      if (startHour > endHour) {
        return setEndDateError("End date must be greater than the Start date")
      }

      if (startHour === endHour && startHour !== 0) {
        return setEndDateError("Start Time and End Time not be same")
      }
      const payload = {
        userId:  user?.userId,
        action: "updateCallWindow",
        callWindowStart: startHour,
        callWindowEnd: endHour,
        requestFor: "U",
      }
      if (user?.voiceInfo?.userType === "reseller") {
        payload.requestFor = callWindowRequestFor;
      }
      setCallWindowLoader(true);
      await axiosPatchCall("updateSmsProfileSetting", payload);
      setCallWindowLoader(false);
      renderSuccessMessage("Success");
    }
    catch (error) {
      logger.error(error);
      setCallWindowLoader(false);
      renderErrorMessage(error.message)
    }
  }

  useEffect(() => {
    fetchSettingDetails();
    fetchCallWindowDetails();
    fetchDetailDownloadCsvOption();
    fetchCustomBroadcastApiAccess();
    if (checkButtonAccess("userTpsSetting", "r")) {
      fetchAllocatedTps();
    }
  }, [load]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        padding: '6px',
        maxWidth: '70%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
      API Message Limit
    </Typography>
      <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <TextField
            fullWidth
            label="Enter API Per Minute Message Limit"
            variant="outlined"
            size="small"
            value={apiPerMessageLimit}
            onChange={(e) => setApiPerMessageLimit(e.target.value)}
            InputProps={{ sx: inputSx }}
          />
          {user?.voiceInfo?.userType === 'reseller' && (
            <FormControl fullWidth size="small">
              <InputLabel>Request For</InputLabel>
              <Select
                value={RequestFor || ''}
                label="Request For"
                onChange={(e) => setRequestFor(e?.target?.value)}
              >
                {requestList.map(({ label, value }) => (
                  <MenuItem key={label} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ pb: '2px' }}>
          <IconButton onClick={() => handleReset('apiPerCallLimit')}>
            <Iconify icon="ix:reset" />
          </IconButton>
          <LoadingButton
            loading={isApiLimitUpdating}
            variant="contained"
            sx={{ width: '100px' }}
            onClick={() => handleSubmit('updatePerMinuteApiCallLimits')}
          >
            Submit
          </LoadingButton>
        </Stack>
      </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
          Hyper API Message Limit
        </Typography>
        <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <TextField
              fullWidth
              label="Enter Hyper API Per Minute Message Limit"
              variant="outlined"
              size="small"
              value={hyperApiPerMessageLimit}
              onChange={(e) => setHyperApiPerMessageLimit(e.target.value)}
              InputProps={{ sx: inputSx }}
            />
            {user?.voiceInfo?.userType === 'reseller' && (
              <FormControl fullWidth size="small">
                <InputLabel>Request For</InputLabel>
                <Select
                  value={hyperRequestFor || ''}
                  label="Request For"
                  onChange={(e) => setHyperRequestFor(e?.target?.value)}
                >
                  {requestList.map(({ label, value }) => (
                    <MenuItem key={label} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Stack direction="row" spacing={1} sx={{ pb: '2px' }}>
            <IconButton onClick={() => handleReset('hyperApiPerCallLimit')}>
              <Iconify icon="ix:reset" />
            </IconButton>
            <LoadingButton
              loading={isHyperApiLimitUpdating}
              variant="contained"
              sx={{ width: '100px' }}
              onClick={() => handleSubmit('hyperUpdatePerMinuteApiCallLimits')}
            >
              Submit
            </LoadingButton>
          </Stack>
        </Box>
      </Box>

      {/* SECTION 2: CALL WINDOW */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
          SMS Window
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: '20px',
            alignItems: 'flex-end',
            border: '1px dashed #cbd5e1',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#f8fafc'
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>

            {/* ROW FOR PICKERS */}
            <Box sx={{ display: 'flex', gap: '15px', width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <BasicDatePicker
                  label="Start Time"
                  value={startTime}
                  onChange={setStartTime}
                  dateError={startDateError}
                  resetError={resetStartTimeError}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <BasicDatePicker
                  label="End Time"
                  value={endTime}
                  onChange={setEndTime}
                  dateError={endDateError}
                  resetError={resetEndTimeError}
                />
              </Box>
            </Box>

            {user?.voiceInfo?.userType === 'reseller' && (
              <FormControl fullWidth size="small">
                <InputLabel>Request For</InputLabel>
                <Select
                  value={callWindowRequestFor || ''}
                  label="Request For"
                  onChange={(e) => setCallWindowRequestFor(e?.target?.value)}
                >
                  {requestList.map(({ label, value }) => (
                    <MenuItem key={label} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ pb: '2px' }}>
            <IconButton onClick={() => handleReset('callWindow')}>
              <Iconify icon="ix:reset" />
            </IconButton>
            <LoadingButton
              loading={callWindowloader}
              variant="contained"
              sx={{ width: '100px' }}
              onClick={() => handleSubmit('callWindow')}
            >
              Submit
            </LoadingButton>
          </Stack>
        </Box>
      </Box>

      {/* SECTION 3: USER TPS SETTING */}
      {checkButtonAccess("userTpsSetting", "r") && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
          User TPS Setting
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: '20px',
            alignItems: 'flex-end',
            border: '1px dashed #cbd5e1',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#f8fafc',
          }}
        >
          <TextField
            fullWidth
            type="number"
            label="Enter User TPS"
            variant="outlined"
            size="small"
            value={userTps}
            onChange={(event) => setUserTps(event.target.value)}
            inputProps={{ min: 1, step: 1 }}
            InputProps={{ sx: inputSx }}
          />
          {checkButtonAccess("userTpsSetting", "w") && ( 
          <Stack direction="row" spacing={1} sx={{ pb: '2px' }}>
            <IconButton onClick={() => handleReset('userTps')}>
              <Iconify icon="ix:reset" />
            </IconButton>
            <LoadingButton
              loading={userTpsLoader}
              variant="contained"
              sx={{ width: '100px' }}
              onClick={() => handleSubmit('userTps')}
            >
              Submit
            </LoadingButton>
          </Stack>
          )}
        </Box>
      </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          p: '16px',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>
          Campaign Details CSV Download
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          When this option is turned off, the CSV download option will be removed from the Campaign Details page for this user. 
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ color: 'grey.700', fontWeight: 500 }}>
            Allow CSV download
          </Typography>
          <SwitchButton
            checked={detailDownloadCsvEnabled}
            onChange={handleDetailDownloadCsvChange}
            disabled={isDetailDownloadCsvLoading || isDetailDownloadCsvUpdating}
            inputProps={{ 'aria-label': 'Campaign details CSV download option' }}
          />
          <Typography
            sx={{
              color: detailDownloadCsvEnabled ? 'success.main' : 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 600,
              minWidth: '70px',
            }}
          >
            {isDetailDownloadCsvLoading
              ? 'Loading...'
              : detailDownloadCsvEnabled
                ? 'Enabled'
                : 'Disabled'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          p: '16px',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>
          Custom Broadcast API
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          When enabled, the Custom Broadcast API will be shown to this user on the API page.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ color: 'grey.700', fontWeight: 500 }}>
            Allow Custom Broadcast API
          </Typography>
          <SwitchButton
            checked={customBroadcastApiEnabled}
            onChange={handleCustomBroadcastApiAccessChange}
            disabled={isCustomBroadcastApiLoading || isCustomBroadcastApiUpdating}
            inputProps={{ 'aria-label': 'Custom Broadcast API access' }}
          />
          <Typography
            sx={{
              color: customBroadcastApiEnabled ? 'success.main' : 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 600,
              minWidth: '70px',
            }}
          >
            {isCustomBroadcastApiLoading
              ? 'Loading...'
              : customBroadcastApiEnabled
                ? 'Enabled'
                : 'Disabled'}
          </Typography>
        </Box>
      </Box>

    </Box>
  );
};

export default SmsCampaignSetting;
